import React from "react";
import { isPlatform } from "@ionic/core";
import { IonContent, IonModal, IonSpinner, useIonToast } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { useAppStore } from "../../../store/app";
import AiSettings from "..";
import {
  IconPlugConnected,
  IconListCheck,
  IconRefresh,
  IconDatabase,
  IconMessageChatbot,
  IconSitemap,
  IconChevronRight,
  IconCheck,
} from "@tabler/icons-react";
import ReactGA from "react-ga4";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";

import styles from "./styles.module.css";
import "./modal.css";
import AiApi from "../../../api/ai";
import Subscription from "../../../api/subscription";
import { TIERS } from "../../usage/management/variables";
import { useAuth } from "../../../providers/auth";

const isMobile = isPlatform("android") || isPlatform("ios");
const VISIBLE_TIERS = TIERS.filter((tier) => !tier.hide);
const STARTER_TIERS = VISIBLE_TIERS.filter((tier) =>
  [2500, 5000, 10000].includes(tier.credits),
);
const PRO_TIERS = VISIBLE_TIERS.filter((tier) =>
  [20000, 40000, 80000, 160000].includes(tier.credits),
);
const DEFAULT_STARTER_TIER = STARTER_TIERS.find((tier) => tier.credits === 2500);
const DEFAULT_PRO_TIER = PRO_TIERS.find((tier) => tier.credits === 20000);

type PlaygroundBehaviorProps = {
  isolated?: boolean;
  onSwitchCompany?: Function;
};

type WelcomePaywallTarget = "channels" | "integrations";

const routeByTarget: Record<WelcomePaywallTarget, string> = {
  channels: "/ai/channels",
  integrations: "/ai/integrations",
};

export default function PlaygroundBehavior({
  isolated = false,
  onSwitchCompany,
}: PlaygroundBehaviorProps) {
  const settings = useAppStore((state) => state.settings);
  const [present] = useIonToast();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { logout } = useAuth();
  const [isPaywallOpen, setIsPaywallOpen] = React.useState(false);
  const [paywallTarget, setPaywallTarget] =
    React.useState<WelcomePaywallTarget>("channels");
  const [starterPriceId, setStarterPriceId] = React.useState(
    DEFAULT_STARTER_TIER?.priceId || STARTER_TIERS[0]?.priceId || "",
  );
  const [proPriceId, setProPriceId] = React.useState(
    DEFAULT_PRO_TIER?.priceId || PRO_TIERS[0]?.priceId || "",
  );
  const [isCheckoutLoading, setIsCheckoutLoading] = React.useState(false);
  const [isMobileWelcomeExpanded, setIsMobileWelcomeExpanded] = React.useState(
    !isMobile,
  );
  const hasTrackedReturnRef = React.useRef(false);
  const isPaymentActive = Boolean(settings.status?.payment?.active);
  const selectedStarterTier =
    STARTER_TIERS.find((tier) => tier.priceId === starterPriceId) ||
    DEFAULT_STARTER_TIER ||
    STARTER_TIERS[0];
  const selectedProTier =
    PRO_TIERS.find((tier) => tier.priceId === proPriceId) ||
    DEFAULT_PRO_TIER ||
    PRO_TIERS[0];

  const { data } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
    enabled: Boolean(isolated),
  });

  React.useEffect(() => {
    if (!isolated || hasTrackedReturnRef.current) return;
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("welcomeCheckout");
    if (checkoutStatus !== "success" && checkoutStatus !== "cancel") return;

    hasTrackedReturnRef.current = true;
    if ((window as any).GA_INITIALIZED) {
      ReactGA.event("welcome_paywall_checkout_returned", {
        result: checkoutStatus,
      });
    }

    history.replace(location.pathname);
  }, [isolated, location.pathname, location.search, history]);

  const openPaywall = (target: WelcomePaywallTarget) => {
    if (isPaymentActive) {
      history.push(routeByTarget[target]);
      return;
    }

    setPaywallTarget(target);
    setStarterPriceId(DEFAULT_STARTER_TIER?.priceId || STARTER_TIERS[0]?.priceId || "");
    setProPriceId(DEFAULT_PRO_TIER?.priceId || PRO_TIERS[0]?.priceId || "");
    setIsPaywallOpen(true);

    if ((window as any).GA_INITIALIZED) {
      ReactGA.event("welcome_paywall_opened", { item: target });
    }
  };

  const onStartCheckout = async (priceId: string) => {
    if (!priceId) return;

    try {
      setIsCheckoutLoading(true);
      if ((window as any).GA_INITIALIZED) {
        ReactGA.event("welcome_paywall_checkout_started", {
          item: paywallTarget,
          tier: priceId,
        });
      }

      const { data: checkoutData, err } = await Subscription.newPlan({
        priceId,
        origin: "welcome",
        target: paywallTarget,
        successUrl: "/ai?welcomeCheckout=success",
        cancelUrl: "/welcome?welcomeCheckout=cancel",
      });

      if (err || !checkoutData?.url) {
        throw err || new Error("Failed to open checkout");
      }

      window.location.href = checkoutData.url;
    } catch (e) {
      present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
      });
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const onOpenEnterpriseQuote = () => {
    const intentLabel = intl.formatMessage({
      id:
        paywallTarget === "channels"
          ? "ai.playground.welcome.paywall.enterprise.intent.channels"
          : "ai.playground.welcome.paywall.enterprise.intent.integrations",
    });
    const subject = intl.formatMessage(
      { id: "ai.playground.welcome.paywall.enterprise.mail.subject" },
      { intent: intentLabel },
    );
    const body = intl.formatMessage(
      { id: "ai.playground.welcome.paywall.enterprise.mail.body" },
      {
        company: settings.company?.name || "-",
        intent: intentLabel,
      },
    );
    window.location.href = `mailto:help@visitoai.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  const showWelcomeSidebar = isolated;

  return (
    <div className={styles.container}>
      <main className={styles.chatStage}>
        <div className={styles.chatWrapper}>
          <AiSettings settings={settings} />
        </div>
      </main>

      {!isMobile && !isolated && (
        <aside className={styles.configSidebar}>
          <div className={styles.header}>
            <h2>
              <FormattedMessage id="ai.playground.help.title" />
            </h2>
            <p className={styles.subtitle}>
              <FormattedMessage id="ai.playground.help.subtitle" />
            </p>
          </div>

          <div className={styles.statusCard}>
            <div className={styles.statusHeader}>
              <IconPlugConnected size={18} className={styles.connectedIcon} />
              <span>
                <FormattedMessage id="ai.playground.help.status.title" />
              </span>
            </div>
            <p className={styles.statusText}>
              <FormattedMessage id="ai.playground.help.status.desc" />
            </p>
          </div>

          <div className={styles.divider} />

          <h3 className={styles.sectionTitle}>
            <IconListCheck size={16} />
            <FormattedMessage id="ai.playground.help.suggestions.title" />
          </h3>

          <ul className={styles.checklist}>
            <li>
              <div className={styles.checkIcon}>
                <IconDatabase size={18} />
              </div>
              <div className={styles.checkContent}>
                <strong>
                  <FormattedMessage id="ai.playground.help.suggestions.accuracy.title" />
                </strong>
                <span>
                  <FormattedMessage id="ai.playground.help.suggestions.accuracy.desc" />
                </span>
              </div>
            </li>

            <li>
              <div className={styles.checkIcon}>
                <IconMessageChatbot size={18} />
              </div>
              <div className={styles.checkContent}>
                <strong>
                  <FormattedMessage id="ai.playground.help.suggestions.tone.title" />
                </strong>
                <span>
                  <FormattedMessage id="ai.playground.help.suggestions.tone.desc" />
                </span>
              </div>
            </li>

            <li>
              <div className={styles.checkIcon}>
                <IconSitemap size={18} />
              </div>
              <div className={styles.checkContent}>
                <strong>
                  <FormattedMessage id="ai.playground.help.suggestions.logic.title" />
                </strong>
                <span>
                  <FormattedMessage id="ai.playground.help.suggestions.logic.desc" />
                </span>
              </div>
            </li>
          </ul>

          <div className={styles.footerNote}>
            <IconRefresh size={16} />
            <p>
              <FormattedMessage id="ai.playground.help.footer" />
            </p>
          </div>
        </aside>
      )}

      {showWelcomeSidebar ? (
        <aside className={styles.welcomeSidebar}>
          <div className={styles.welcomeHeader}>
            <div className={styles.welcomeHeaderContent}>
              <h3>
                <FormattedMessage id="ai.playground.welcome.next.title" />
              </h3>
            </div>
            {isMobile && (
              <button
                type="button"
                className={styles.welcomeHeaderToggle}
                onClick={() => setIsMobileWelcomeExpanded((prev) => !prev)}
                aria-controls="welcome-next-steps-content"
                aria-expanded={isMobileWelcomeExpanded}
                aria-label={intl.formatMessage({
                  id: "ai.playground.welcome.next.toggle",
                })}
              >
                <IconChevronRight
                  size={18}
                  className={`${styles.welcomeHeaderChevron} ${
                    isMobileWelcomeExpanded ? styles.welcomeHeaderChevronOpen : ""
                  }`}
                />
              </button>
            )}
          </div>

          {(!isMobile || isMobileWelcomeExpanded) && (
            <div
              id="welcome-next-steps-content"
              className={styles.welcomeStepsContent}
            >
              <div className={styles.welcomeStepCard}>
                <div className={styles.welcomeStepHead}>
                  <div className={styles.welcomeItemBody}>
                    <strong>
                      <FormattedMessage id="ai.playground.welcome.next.configure.title" />
                    </strong>
                    <span>
                      <FormattedMessage id="ai.playground.welcome.next.configure.desc" />
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className={`btn btn-visito-primary btn-sm ${styles.welcomeStepButton}`}
                  onClick={() => openPaywall("channels")}
                >
                  <FormattedMessage id="ai.playground.welcome.next.configure.cta" />
                  <IconChevronRight size={14} />
                </button>
              </div>

              <div className={styles.welcomeStepCard}>
                <div className={styles.welcomeStepHead}>
                  <div className={styles.welcomeItemBody}>
                    <strong>
                      <FormattedMessage id="ai.playground.welcome.next.integrations.title" />
                    </strong>
                    <span>
                      <FormattedMessage id="ai.playground.welcome.next.integrations.desc" />
                    </span>
                  </div>
                </div>

                <div className={styles.welcomeLogosRow}>
                  <span className={`${styles.welcomeLogo} cloudbeds-logo`} />
                  <span className={`${styles.welcomeLogo} siteminder-logo`} />
                  <span className={`${styles.welcomeLogo} mirai-logo`} />
                  <span className={`${styles.welcomeLogo} guesty-logo`} />
                  <span className={`${styles.welcomeLogo} shopify-logo`} />
                  <span className={`${styles.welcomeLogo} woocommerce-logo`} />
                </div>

                <div className={styles.welcomeStepActions}>
                  <button
                    type="button"
                    className={`btn btn-visito-primary btn-sm ${styles.welcomeStepButton}`}
                    onClick={() => openPaywall("integrations")}
                  >
                    <FormattedMessage id="ai.playground.welcome.next.integrations.cta" />
                    <IconChevronRight size={14} />
                  </button>
                </div>
              </div>

              <div className={styles.welcomeStepCard}>
                <div className={styles.welcomeStepHead}>
                  <div className={styles.welcomeItemBody}>
                    <strong>
                      <FormattedMessage id="ai.playground.welcome.next.channels.title" />
                    </strong>
                    <span>
                      <FormattedMessage id="ai.playground.welcome.next.channels.desc" />
                    </span>
                  </div>
                </div>

                <div className={styles.welcomeLogosRow}>
                  <span className={`${styles.welcomeLogo} whatsapp-logo`} />
                  <span className={`${styles.welcomeLogo} instagram-logo`} />
                  <span className={`${styles.welcomeLogo} ${styles.welcomeWebLogo}`} />
                </div>

                <div className={styles.welcomeStepActions}>
                  <button
                    type="button"
                    className={`btn btn-visito-primary btn-sm ${styles.welcomeStepButton}`}
                    onClick={() => openPaywall("channels")}
                  >
                    <FormattedMessage id="ai.playground.welcome.next.channels.cta" />
                    <IconChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={styles.welcomeSidebarFooter}>
            {settings.companies.length > 1 && onSwitchCompany ? (
              <label className={styles.welcomeAccountSwitcher}>
                <span className={styles.welcomeAccountLabel}>
                  <FormattedMessage id="common.account" />
                </span>
                <select
                  className={styles.welcomeAccountSelect}
                  value={settings.company.id}
                  onChange={(e) => {
                    const nextCompanyId = e.target.value;
                    if (!nextCompanyId || nextCompanyId === settings.company.id) {
                      return;
                    }
                    onSwitchCompany({ companyId: nextCompanyId });
                  }}
                >
                  {settings.companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              className={styles.welcomeLogoutLink}
              onClick={logout}
            >
              <FormattedMessage id="nav.logout" />
            </button>
          </div>
        </aside>
      ) : null}

      <IonModal
        isOpen={isPaywallOpen}
        className="welcome-paywall-modal"
        backdropDismiss={!isCheckoutLoading}
        canDismiss={!isCheckoutLoading}
        onWillDismiss={() => setIsPaywallOpen(false)}
      >
        <IonContent className="ion-padding">
          <div className={styles.welcomePaywallModal}>
            <div className={styles.welcomePaywallHeader}>
              <h3>
                <FormattedMessage id="ai.playground.welcome.paywall.title" />
              </h3>
              <p>
                <FormattedMessage id="ai.playground.welcome.paywall.desc" />
              </p>
            </div>

            <div className={styles.welcomePricingGrid}>
              <div
                className={`${styles.welcomePricingCard} ${styles.welcomePricingFeatured}`}
              >
                <div className={styles.welcomePricingTop}>
                  <div className={styles.welcomePricingTitleRow}>
                    <h4>
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.title" />
                    </h4>
                    <span className={styles.welcomePricingBadge}>
                      <FormattedMessage id="ai.playground.welcome.paywall.mostPopular" />
                    </span>
                  </div>
                  <p>
                    <FormattedMessage id="ai.playground.welcome.paywall.starter.desc" />
                  </p>
                </div>

                <div className={styles.welcomePricingPriceBox}>
                  <div className={styles.welcomePricingPriceLine}>
                    <strong>
                      {intl.formatNumber(selectedStarterTier?.priceUsd || 0, {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                    <span>
                      <FormattedMessage id="ai.playground.welcome.paywall.perMonth" />
                    </span>
                  </div>
                  <label
                    className={styles.welcomePricingLabel}
                    htmlFor="welcome-starter-plan"
                  >
                    <FormattedMessage id="ai.playground.welcome.paywall.creditOptions" />
                  </label>
                  <select
                    id="welcome-starter-plan"
                    className={styles.welcomePaywallSelect}
                    value={starterPriceId}
                    onChange={(e) => setStarterPriceId(e.target.value)}
                  >
                    {STARTER_TIERS.map((tier) => (
                      <option key={tier.priceId} value={tier.priceId}>
                        {intl.formatMessage(
                          { id: "ai.playground.welcome.paywall.creditsOption" },
                          { credits: tier.credits },
                        )}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.welcomePricingIncludes}>
                  <h5>
                    <FormattedMessage id="ai.playground.welcome.paywall.includes" />
                  </h5>
                  <ul>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.1" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.2" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.3" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.4" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.5" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.starter.feature.6" />
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  className="btn btn-visito-primary"
                  onClick={() => onStartCheckout(starterPriceId)}
                  disabled={isCheckoutLoading || !starterPriceId}
                >
                  {isCheckoutLoading ? (
                    <>
                      <IonSpinner
                        color="light"
                        name="lines-sharp-small"
                        style={{
                          width: "14px",
                          height: "14px",
                          marginRight: "6px",
                        }}
                      />
                      <FormattedMessage id="common.loading" />
                    </>
                  ) : (
                    <FormattedMessage id="ai.playground.welcome.paywall.cta" />
                  )}
                </button>
              </div>

              <div className={styles.welcomePricingCard}>
                <div className={styles.welcomePricingTop}>
                  <h4>
                    <FormattedMessage id="ai.playground.welcome.paywall.pro.title" />
                  </h4>
                  <p>
                    <FormattedMessage id="ai.playground.welcome.paywall.pro.desc" />
                  </p>
                </div>

                <div className={styles.welcomePricingPriceBox}>
                  <div className={styles.welcomePricingPriceLine}>
                    <strong>
                      {intl.formatNumber(selectedProTier?.priceUsd || 0, {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                    <span>
                      <FormattedMessage id="ai.playground.welcome.paywall.perMonth" />
                    </span>
                  </div>
                  <label
                    className={styles.welcomePricingLabel}
                    htmlFor="welcome-pro-plan"
                  >
                    <FormattedMessage id="ai.playground.welcome.paywall.creditOptions" />
                  </label>
                  <select
                    id="welcome-pro-plan"
                    className={styles.welcomePaywallSelect}
                    value={proPriceId}
                    onChange={(e) => setProPriceId(e.target.value)}
                  >
                    {PRO_TIERS.map((tier) => (
                      <option key={tier.priceId} value={tier.priceId}>
                        {intl.formatMessage(
                          { id: "ai.playground.welcome.paywall.creditsOption" },
                          { credits: tier.credits },
                        )}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.welcomePricingIncludes}>
                  <h5>
                    <FormattedMessage id="ai.playground.welcome.paywall.pro.includes" />
                  </h5>
                  <ul>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.1" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.2" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.3" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.4" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.5" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.pro.feature.6" />
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  className="btn btn-visito-light"
                  onClick={() => onStartCheckout(proPriceId)}
                  disabled={isCheckoutLoading || !proPriceId}
                >
                  {isCheckoutLoading ? (
                    <>
                      <IonSpinner
                        color="medium"
                        name="lines-sharp-small"
                        style={{
                          width: "14px",
                          height: "14px",
                          marginRight: "6px",
                        }}
                      />
                      <FormattedMessage id="common.loading" />
                    </>
                  ) : (
                    <FormattedMessage id="ai.playground.welcome.paywall.cta" />
                  )}
                </button>
              </div>

              <div className={styles.welcomePricingCard}>
                <div className={styles.welcomePricingTop}>
                  <h4>
                    <FormattedMessage id="ai.playground.welcome.paywall.enterprise.title" />
                  </h4>
                  <p>
                    <FormattedMessage id="ai.playground.welcome.paywall.enterprise.desc" />
                  </p>
                </div>

                <div className={styles.welcomePricingPriceBox}>
                  <div className={styles.welcomePricingCustom}>
                    <FormattedMessage id="ai.playground.welcome.paywall.enterprise.custom" />
                  </div>
                </div>

                <div className={styles.welcomePricingIncludes}>
                  <h5>
                    <FormattedMessage id="ai.playground.welcome.paywall.enterprise.includes" />
                  </h5>
                  <ul>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.enterprise.feature.1" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.enterprise.feature.2" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.enterprise.feature.3" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.enterprise.feature.4" />
                    </li>
                    <li>
                      <IconCheck size={14} />
                      <FormattedMessage id="ai.playground.welcome.paywall.enterprise.feature.5" />
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  className="btn btn-visito-light"
                  onClick={onOpenEnterpriseQuote}
                  disabled={isCheckoutLoading}
                >
                  <FormattedMessage id="ai.playground.welcome.paywall.enterprise.cta" />
                </button>
              </div>
            </div>

            <div className={styles.welcomeCreditsInfo}>
              <h4>
                <FormattedMessage id="ai.playground.welcome.paywall.creditsInfo.title" />
              </h4>
              <p>
                <FormattedMessage id="ai.playground.welcome.paywall.creditsInfo.body" />
              </p>
            </div>

            {isMobile && (
              <div className={styles.welcomePaywallActions}>
                <button
                  type="button"
                  className="btn btn-visito-light btn-sm"
                  onClick={() => setIsPaywallOpen(false)}
                  disabled={isCheckoutLoading}
                >
                  <FormattedMessage id="common.cancel" />
                </button>
              </div>
            )}
          </div>
        </IonContent>
      </IonModal>
    </div>
  );
}
